import { useState } from "react";
import { KeyRound, Loader2, Mail } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputClass =
  "h-11 w-full rounded-md border border-clay bg-white px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const labelClass = "mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta";

const AdminAccount = ({ user, onEmailChanged }) => {
  const [email, setEmail] = useState(user?.email || "");
  const [savingEmail, setSavingEmail] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const saveEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      const { data } = await api.put("/auth/account/email", { new_email: email });
      onEmailChanged?.(data.email);
      toast.success("Email de connexion mis à jour — utilisez-le à votre prochaine connexion");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 8) {
      toast.error("Le nouveau mot de passe doit faire au moins 8 caractères");
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe");
      return;
    }
    setSavingPw(true);
    try {
      await api.put("/auth/account/password", { current_password: pwForm.current, new_password: pwForm.next });
      setPwForm({ current: "", next: "", confirm: "" });
      toast.success("Mot de passe modifié — utilisez-le à votre prochaine connexion");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <section data-testid="admin-account-section" className="border border-clay bg-cream p-6">
      <h2 className="font-display text-xl font-semibold text-bark">Mon compte</h2>
      <p className="mt-1 text-xs text-bark/50">Modifiez vos identifiants de connexion à l'espace administrateur.</p>

      <form onSubmit={saveEmail} className="mt-6 space-y-3">
        <div>
          <label className={labelClass}>
            <Mail className="mr-1 inline h-3.5 w-3.5" /> Email de connexion
          </label>
          <input
            data-testid="admin-account-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            placeholder="admin@sama-keur.sn"
          />
        </div>
        <button
          data-testid="admin-account-email-save"
          type="submit"
          disabled={savingEmail || email.trim().toLowerCase() === user?.email}
          className="flex h-11 items-center gap-2 rounded-full bg-bark px-6 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta disabled:opacity-50"
        >
          {savingEmail && <Loader2 className="h-4 w-4 animate-spin" />}
          Mettre à jour l'email
        </button>
      </form>

      <form onSubmit={savePassword} className="mt-8 space-y-3 border-t border-clay pt-6">
        <p className={labelClass}>
          <KeyRound className="mr-1 inline h-3.5 w-3.5" /> Changer le mot de passe
        </p>
        <input
          data-testid="admin-account-current-password-input"
          type="password"
          required
          value={pwForm.current}
          onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))}
          className={inputClass}
          placeholder="Mot de passe actuel *"
          autoComplete="current-password"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <input
            data-testid="admin-account-new-password-input"
            type="password"
            required
            minLength={8}
            value={pwForm.next}
            onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))}
            className={inputClass}
            placeholder="Nouveau mot de passe (8 car. min) *"
            autoComplete="new-password"
          />
          <input
            data-testid="admin-account-confirm-password-input"
            type="password"
            required
            minLength={8}
            value={pwForm.confirm}
            onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))}
            className={inputClass}
            placeholder="Confirmer le nouveau mot de passe *"
            autoComplete="new-password"
          />
        </div>
        <button
          data-testid="admin-account-password-save"
          type="submit"
          disabled={savingPw || !pwForm.current || !pwForm.next}
          className="flex h-11 items-center gap-2 rounded-full bg-terracotta px-6 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
        >
          {savingPw && <Loader2 className="h-4 w-4 animate-spin" />}
          Changer le mot de passe
        </button>
      </form>
    </section>
  );
};

export default AdminAccount;
